import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, X } from "lucide-react";
import { trackCta } from "@/lib/analytics";

const DISMISS_KEY = "kore_mobile_cta_dismissed_v2";
const COOKIE_KEY = "kore_cookie_ok";
const SHOW_AFTER_PX = 600; // must scroll past the hero first

export default function MobileCTABar() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [cookieOk, setCookieOk] = useState(true);

  // Read persisted flags once.
  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
      setCookieOk(!!localStorage.getItem(COOKIE_KEY));
    } catch { /* noop */ }
  }, []);

  // Re-check cookie acceptance on storage changes (accept button in another tab)
  // AND on a light poll — the cookie banner writes localStorage synchronously
  // but doesn't emit a custom event.
  useEffect(() => {
    const check = () => {
      try { setCookieOk(!!localStorage.getItem(COOKIE_KEY)); } catch { /* noop */ }
    };
    const id = setInterval(check, 500);
    window.addEventListener("storage", check);
    return () => { clearInterval(id); window.removeEventListener("storage", check); };
  }, []);

  // Reveal only after the hero has scrolled off.
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pathname.startsWith("/admin") || pathname.startsWith("/scorecard")) return null;
  if (pathname === "/trial" || pathname === "/30-day-trial") return null;
  if (pathname === "/memberships/kore-coached") return null;
  if (pathname === "/memberships/kore-access") return null;
  if (!cookieOk) return null;
  if (!visible || dismissed) return null;

  const handleClick = () => {
    trackCta({
      cta_name: "sticky_mobile_reset",
      cta_text: "Start your 30 Day Trial",
      destination_url: "/30-day-trial",
      cta_location: "sticky_mobile",
    });
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="mobile-cta-bar"
      role="region"
      aria-label="Start the 30-day Coached Trial"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-kore-warm/95 backdrop-blur-md border-t border-kore-gold/20 shadow-[0_-6px_20px_-8px_rgba(0,0,0,0.15)]"
    >
      <div className="p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] flex items-center gap-2">
        <Link
          to="/30-day-trial"
          onClick={handleClick}
          data-testid="mobile-cta-trial"
          className="flex-1 min-h-[44px] flex items-center justify-center gap-2 rounded-full bg-kore-forest text-kore-warm text-[13px] font-medium tracking-wide py-3.5 px-4"
        >
          <Calendar size={15} /> Start your 30 Day Trial
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss trial bar"
          data-testid="mobile-cta-dismiss"
          className="shrink-0 w-11 h-11 flex items-center justify-center rounded-full text-kore-forest/70 hover:text-kore-forest border border-kore-gold/25"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
